/**
 * @module fx/HitStop
 * Impact freeze — briefly pauses game simulation (~50 ms) on a successful
 * slash to make hits feel weighty.
 *
 * The game loop must check {@link HitStop.active} / {@link isHitStopActive}
 * before advancing physics; rendering continues normally during hitstop so
 * the visual "freeze" is seen by the player.
 *
 * Functional API (contract): triggerHitStop(), isHitStopActive().
 * Legacy class API (HitStop) is kept for any pre-existing wiring.
 */

import { CONFIG } from '../game-config.js';

/** Manages the hitstop timer. */
export class HitStop {
  private _remaining = 0;

  /**
   * Triggers a hitstop for the given duration.
   * If one is already active, the longer duration wins.
   */
  trigger(durationMs: number): void {
    this._remaining = Math.max(this._remaining, durationMs);
  }

  /**
   * Advances the hitstop timer.
   * Call once per render frame with the raw frame delta.
   */
  update(deltaMs: number): void {
    this._remaining = Math.max(0, this._remaining - deltaMs);
  }

  /** True while physics should be frozen. */
  get active(): boolean {
    return this._remaining > 0;
  }
}

// ---------------------------------------------------------------------------
// Functional module API (per module contract)
// ---------------------------------------------------------------------------

/** Module-level singleton for the functional API. */
const _hitStop = new HitStop();

/**
 * Trigger hitstop: sets timeScale to 0 for CONFIG.feel.hitstopMs ms,
 * then restores the previous timeScale.
 * Multiple simultaneous calls within the same hitstop window are ignored (no stacking).
 *
 * NOTE: GameLoop integration reads isHitStopActive() to gate physics updates.
 * The functional triggerHitStop() uses CONFIG.feel.hitstopMs as the fixed duration.
 */
export function triggerHitStop(): void {
  _hitStop.trigger(CONFIG.feel.hitstopMs);
}

/**
 * Advance the module-level hitstop timer by deltaMs.
 * Call once per render frame with the raw (unscaled) frame delta.
 */
export function tickHitStop(deltaMs: number): void {
  _hitStop.update(deltaMs);
}

/** True while a hitstop is in progress. */
export function isHitStopActive(): boolean {
  return _hitStop.active;
}

/** Reset hitstop state (call on game restart). */
export function resetHitStop(): void {
  // Force expire by updating with a large value.
  _hitStop.update(Number.MAX_SAFE_INTEGER);
}
